import React from "react"
import { useBlogOverviewHeroQuery } from "../hooks/use-blog-overview-hero-query"
import { useBlogPrevNextTextQuery } from "../hooks/use-blog-prev-next-text-query"
import { useCertificationsQuery } from "../hooks/use-certifications-query"
import { useContactQuery } from "../hooks/use-contact-query"
import { useFeaturedServicesQuery } from "../hooks/use-featured-services"
import { useFooterQuery } from "../hooks/use-footer-query"
import { useGeneralSchemaQuery } from "../hooks/use-general-schema-query"
import { useHeaderQuery } from "../hooks/use-header-query"
import { useHomeBlogsQuery } from "../hooks/use-home-blogs-query"
import { useHomeSeoQuery } from "../hooks/use-home-seo-query"
import { useHomeCalloutQuery } from "../hooks/use-home-callout-query"
import { useHomeCouponsQuery } from "../hooks/use-home-coupons"
import { useInteriorCalloutQuery } from "../hooks/use-interior-callout-query"
import { usePageSpecificSchemaQuery } from "../hooks/use-page-specific-schema-query"
import { useServiceAreasQuery } from "../hooks/use-service-areas-query"
import { useServicesQuery } from "../hooks/use-services-query"
import { useTestimonialsQuery } from "../hooks/use-testimonials-query"
import { useWelcomeQuery } from "../hooks/use-welcome-query"
import { useWhyChooseQuery } from "../hooks/use-why-choose-query"
import { useTopBarMenuQuery } from "../hooks/use-top-bar-menu-query"
import { useMainMenuQuery } from "../hooks/use-main-menu-query"
import { useFooterMenuQuery } from "../hooks/use-footer-menu-query"
import { useHomeHeroQuery } from "../hooks/use-home-hero-query"
import { useMegaMenuButtonsQuery } from "../hooks/use-mega-menu-buttons-query"
import { usePageCommonItemsQuery } from "../hooks/use-page-common-items"
import { useHomeFinancingQuery } from "../hooks/use-home-financing-query"
import { useButtonTextQuery } from "../hooks/use-button-text-query"

export function useHomeHeroData() {
  const { secondaryHeroImage, heroVideo } = useHomeHeroQuery()
  const finalHomeHeroData = {
    secondaryHeroImage:
      secondaryHeroImage.acfOptionsCommonItems.commonItems
        .commonItemsSecondaryHeroImage.localFile.childImageSharp
        .gatsbyImageData,
    secondaryHeroImageAltText:
      secondaryHeroImage.acfOptionsCommonItems.commonItems
        .commonItemsSecondaryHeroImageAltText,
    backgroundImage:
      heroVideo.herovideo.heroVideoBackgroundImage.localFile.childImageSharp
        .gatsbyImageData.images.fallback.src,
    textColored: heroVideo.herovideo.heroVideoTextColored,
    textWhite: heroVideo.herovideo.heroVideoTextWhite,
    buttonText: heroVideo.herovideo.heroVideoButton.title,
    buttonLink: heroVideo.herovideo.heroVideoButton.url,
    playButton:
      heroVideo.herovideo.heroVideoPlayButton?.localFile.childSvg.content.data,
    backgroundVideoUrl: heroVideo.herovideo.heroVideoBackgroundVideoUrl?.url,
    fullVideoUrl: heroVideo.herovideo.heroVideoFullVideoUrl?.url,
  }
  return finalHomeHeroData
}
export function useMegaMenuButtonsData() {
  const { secondButton } = useMegaMenuButtonsQuery()

  const megaMenuButtons = {
    secondButtonText: secondButton.herovideo.heroVideoButton.title,
    secondButtonLink: secondButton.herovideo.heroVideoButton.url,
  }
  return megaMenuButtons
}

export function useBlogOverviewHeroData() {
  const blogOverviewHero = useBlogOverviewHeroQuery()

  const finalBlogOverviewHeroData = {
    backgroundImage:
      blogOverviewHero.commonItemsBlogOverviewHeroBackgroundImage?.localFile
        .childImageSharp.gatsbyImageData,
    altText:
      blogOverviewHero?.commonItemsBlogOverviewHeroBackgroundImageAltText,
    title: blogOverviewHero?.commonItemsBlogOverviewHeroTitle,
    text: blogOverviewHero?.commonItemsBlogOverviewHeroText,
  }

  return finalBlogOverviewHeroData
}

export function useBlogPrevNextTextData() {
  const blogPrevNextText = useBlogPrevNextTextQuery()

  const finalBlogPrevNextTextData = {
    prevText: blogPrevNextText.commonItemsBlogPostPrevText,
    nextText: blogPrevNextText.commonItemsBlogPostNextText,
  }
  return finalBlogPrevNextTextData
}

export function useCertificationsData() {
  const certificationsData = useCertificationsQuery()

  const finalCertificationsData = {
    heading: certificationsData.certificationsHeading,
    certifications:
      certificationsData.certificationsLogos?.length &&
      certificationsData.certificationsLogos.map((certificationItem, i) => {
        return {
          index: i,
          logo: certificationItem.certificationsLogo.localFile.childImageSharp
            .gatsbyImageData,
          altText: certificationItem.certificationsLogoAltText,
        }
      }),
  }

  return finalCertificationsData
}

export function useContactData() {
  const contact = useContactQuery()

  const finalContactData = {
    backgroundImageMobile:
      contact.contactBackgroundImageMobile.localFile.childImageSharp
        .gatsbyImageData,
    backgroundImageDesktop:
      contact.contactBackgroundImageDesktop.localFile.childImageSharp
        .gatsbyImageData,
    altText: contact.contactBackgroundImageAltText,
    heading: contact.contactHeading,
    subheading: contact.contactSubheading,
  }

  return finalContactData
}

export function useFeaturedServicesData() {
  const featuredServicesData = useFeaturedServicesQuery()

  const finalFeaturedServicesData = {
    heading: featuredServicesData.featuredServicesHeading,
    subheading: featuredServicesData.featuredServicesSubheading,
    content: featuredServicesData.featuredServicesContent,
    image:
      featuredServicesData.featuredServicesBottomImage.localFile.childImageSharp
        .gatsbyImageData,
    altText: featuredServicesData.featuredServicesBottomImageAltText,
    featuredServices:
      featuredServicesData.featuredServicesServices?.length &&
      featuredServicesData.featuredServicesServices.map((service, i) => {
        return {
          index: i,
          icon: service.featuredServicesServicesIcon.localFile.childSvg.content
            .data,
          label: service.featuredServicesServicesLabel,
          image:
            service.featuredServicesServicesImage.localFile.childImageSharp
              .gatsbyImageData,
          altText: service.featuredServicesServicesImageAltText,
          content: service.featuredServicesServicesContent,
        }
      }),
  }
  return finalFeaturedServicesData
}

export function useFooterData() {
  const footerData = useFooterQuery()
  const { footerMainMenu, footerTopBarMenu } = useFooterMenuQuery()

  const finalFooterData = {
    logo: footerData.commonItemsLogo.localFile?.childImageSharp.gatsbyImageData,
    text: footerData.commonItemsFooterText,
    section1Title: footerData.commonItemsFooterNavTitle,
    section2Title: footerData.commonItemsFooterContactTitle,
    section3Title: footerData.commonItemsOfficeHoursTitle,
    section3Text: footerData.commonItemsOfficeHoursText,
    section4Title: footerData.commonItemsLicensesTitle,
    section4Text: footerData.commonItemsLicensesText,
    copyright: footerData.commonItemsCopyright,
    mainMenu:
      footerMainMenu?.nodes.length &&
      footerMainMenu.nodes.map((menuItem, i) => {
        return {
          index: i,
          label: menuItem.label,
          path: menuItem.path,
        }
      }),
    topBarMenu:
      footerTopBarMenu?.nodes.length &&
      footerTopBarMenu.nodes.map((menuItem, i) => {
        return {
          index: i,
          label: menuItem.label,
          path: menuItem.path,
        }
      }),
    contactInfo:
      footerData.commonItemsFooterContactInfo?.length &&
      footerData.commonItemsFooterContactInfo.map((contactInfo, i) => {
        return {
          index: i,
          addressLabel: contactInfo.commonItemsLabel1,
          address: contactInfo.commonItemsAddress,
          addressLink: contactInfo.commonItemsLink.url,
          phoneLabel: contactInfo.commonItemsLabel2,
          phoneNumber: contactInfo.commonItemsPhoneNumber,
        }
      }),
    socialMedia:
      footerData.commonItemsSocialMedia?.length &&
      footerData.commonItemsSocialMedia.map((social, i) => {
        return {
          index: i,
          icon: social.commonItemsSocialMediaIcon.localFile.childSvg.content
            .data,
          link: social.commonItemsSocialMediaLink.url,
        }
      }),
  }
  return finalFooterData
}

export function useHomeSeoData() {
  const homeSeo = useHomeSeoQuery()

  const finalHomeSeoData = {
    title: homeSeo.title,
    metaDescription: homeSeo.metaDesc,
    ogTitle: homeSeo.opengraphTitle,
    ogDescription: homeSeo.opengraphDescription,
    ogUrl: homeSeo.opengraphUrl,
    ogSiteName: homeSeo.opengraphSiteName,
    ogType: homeSeo.opengraphType,
    ogImage: homeSeo.opengraphImage?.localFile.publicURL,
    canonicalUrl: homeSeo.canonical,
    twitterTitle: homeSeo.twitterTitle,
    twitterDescription: homeSeo.twitterDescription,
    twitterImage:
      homeSeo.twitterImage?.localFile.childImageSharp.gatsbyImageData,
  }
  return finalHomeSeoData
}

export function useSeoData(data) {
  const seoData = data.wpPage !== undefined ? data.wpPage : data.wpPost
  const finalSeoData = {
    title: seoData.seo.title,
    metaDescription: seoData.seo.metaDesc,
    ogTitle: seoData.seo.opengraphTitle,
    ogDescription: seoData.seo.opengraphDescription,
    ogUrl: seoData.seo.opengraphUrl,
    ogSiteName: seoData.seo.opengraphSiteName,
    ogType: seoData.seo.opengraphType,
    ogImage: seoData.seo.opengraphImage?.localFile.publicURL,
    canonicalUrl: seoData.seo.canonical,
    twitterTitle: seoData.seo.twitterTitle,
    twitterDescription: seoData.seo.twitterDescription,
    twitterImage:
      seoData.seo.twitterImage?.localFile.childImageSharp.gatsbyImageData,
  }
  return finalSeoData
}

export function useInteriorHeroData(data) {
  const commonItems = usePageCommonItemsQuery()
  const pageData = data.wpPage
  const finalPageData = {
    featuredImage:
      pageData.featuredImage !== null || undefined
        ? pageData.featuredImage.node.localFile.childImageSharp.gatsbyImageData
        : commonItems.commonItemsInteriorHeroImageDefault.localFile
            .childImageSharp.gatsbyImageData,
    altText:
      pageData.featuredImageAltText.featuredImageAltText !== null || undefined
        ? pageData.featuredImageAltText.featuredImageAltText
        : commonItems.commonItemsInteriorHeroImageDefaultAltText,
    title: pageData.title,
    buttonText: commonItems.commonItemsButtonText,
  }
  return finalPageData
}
export function usePageData(data) {
  const commonItems = usePageCommonItemsQuery()
  const pageData = data.wpPage
  const finalPageData = {
    interiorSection1: {
      headings: pageData.interiorSection1.interiorSection1Headings,
      content: pageData.interiorSection1.interiorSection1Content,
    },
    interiorSection2: {
      imageSectionHeading:
        pageData.interiorSection2.interiorSection2ImageTextHeading,
      imageSectionText: pageData.interiorSection2.interiorSection2ImageText,
      image:
        pageData.interiorSection2.interiorSection2Image !== (null || undefined)
          ? pageData.interiorSection2.interiorSection2Image?.localFile
              .childImageSharp.gatsbyImageData
          : commonItems.commonItemsInteriorContentImageDefault.localFile
              .childImageSharp.gatsbyImageData,
      altText:
        pageData.interiorSection2.interiorSection2Image !== (null || undefined)
          ? pageData.interiorSection2.interiorSection2ImageAltText
          : commonItems.commonItemsInteriorContentImageDefaultAltText,
      column1Heading: pageData.interiorSection2.interiorSection2HeadingColumn1,
      column1Content: pageData.interiorSection2.interiorSection2ContentColumn1,
      column2Heading: pageData.interiorSection2.interiorSection2HeadingColumn2,
      column2Content: pageData.interiorSection2.interiorSection2ContentColumn2,
    },
    uri: pageData.uri,
    pageTitle: pageData.title,
    callout: {
      image:
        commonItems.commonItemsInteriorCalloutImage.localFile.childImageSharp
          .gatsbyImageData,
      altText: commonItems.commonItemsInteriorCalloutImageAltText,
      text: commonItems.commonItemsInteriorCalloutText,
      linkText: commonItems.commonItemsInteriorCalloutLink.title,
      link: commonItems.commonItemsInteriorCalloutLink.url,
    },
    interiorSection3: {
      heading: pageData.interiorSection3.interiorSection3Heading,
      content: pageData.interiorSection3.interiorSection3Content,
    },
  }
  return finalPageData
}

export function GeneralSchemaFunction() {
  const { wp, site, query1, forRatingValuePart1, forRatingValuePart2 } =
    useGeneralSchemaQuery()

  const data = wp.nexvelSchemaMarkup.nexvelschema

  return {
    services: data.services,
    cities: data.cities,
    businessName: data.businessName,
    siteUrl: site.siteMetadata.siteUrl,
    businessType: data.businessType,
    logo: data.logo.localFile.publicURL,
    image: data.image.localFile.publicURL,
    businessDescription: data.describeYourBusiness,
    phoneNumber: data.phoneNumber,
    address: data.streetAddress,
    city: data.city,
    state: data.state,
    zip: data.zipCode,
    openSunday: data.hoursOfOperation.sunday.areYouOpen,
    sundayClosingTime: `${data.hoursOfOperation.sunday.close.closeHour}:${data.hoursOfOperation.sunday.close.closeMinute} ${data.hoursOfOperation.sunday.close.amorpm}`,
    sundayOpeningTime: `${data.hoursOfOperation.sunday.open.openHour}:${data.hoursOfOperation.sunday.open.openMinute} ${data.hoursOfOperation.sunday.open.amorpm}`,
    openMonday: data.hoursOfOperation.monday.areYouOpen,
    mondayClosingTime: `${data.hoursOfOperation.monday.close.closeHour}:${data.hoursOfOperation.monday.close.closeMinute} ${data.hoursOfOperation.monday.close.amorpm}`,
    mondayOpeningTime: `${data.hoursOfOperation.monday.open.openHour}:${data.hoursOfOperation.monday.open.openMinute} ${data.hoursOfOperation.monday.open.amorpm}`,
    openTuesday: data.hoursOfOperation.tuesday.areYouOpen,
    tuesdayClosingTime: `${data.hoursOfOperation.tuesday.close.closeHour}:${data.hoursOfOperation.tuesday.close.closeMinute} ${data.hoursOfOperation.tuesday.close.amorpm}`,
    tuesdayOpeningTime: `${data.hoursOfOperation.tuesday.open.openHour}:${data.hoursOfOperation.tuesday.open.openMinute} ${data.hoursOfOperation.tuesday.open.amorpm}`,
    openWednesday: data.hoursOfOperation.wednesday.areYouOpen,
    wednesdayClosingTime: `${data.hoursOfOperation.wednesday.close.closeHour}:${data.hoursOfOperation.wednesday.close.closeMinute} ${data.hoursOfOperation.wednesday.close.amorpm}`,
    wednesdayOpeningTime: `${data.hoursOfOperation.wednesday.open.openHour}:${data.hoursOfOperation.wednesday.open.openMinute} ${data.hoursOfOperation.wednesday.open.amorpm}`,
    openThursday: data.hoursOfOperation.thursday.areYouOpen,
    thursdayClosingTime: `${data.hoursOfOperation.thursday.close.closeHour}:${data.hoursOfOperation.thursday.close.closeMinute} ${data.hoursOfOperation.thursday.close.amorpm}`,
    thursdayOpeningTime: `${data.hoursOfOperation.thursday.open.openHour}:${data.hoursOfOperation.thursday.open.openMinute} ${data.hoursOfOperation.thursday.open.amorpm}`,
    openFriday: data.hoursOfOperation.friday.areYouOpen,
    fridayClosingTime: `${data.hoursOfOperation.friday.close.closeHour}:${data.hoursOfOperation.friday.close.closeMinute} ${data.hoursOfOperation.friday.close.amorpm}`,
    fridayOpeningTime: `${data.hoursOfOperation.friday.open.openHour}:${data.hoursOfOperation.friday.open.openMinute} ${data.hoursOfOperation.friday.open.amorpm}`,
    openSaturday: data.hoursOfOperation.saturday.areYouOpen,
    saturdayClosingTime: `${data.hoursOfOperation.saturday.close.closeHour}:${data.hoursOfOperation.saturday.close.closeMinute} ${data.hoursOfOperation.saturday.close.amorpm}`,
    saturdayOpeningTime: `${data.hoursOfOperation.saturday.open.openHour}:${data.hoursOfOperation.saturday.open.openMinute} ${data.hoursOfOperation.saturday.open.amorpm}`,
    reviewsForRatingValuePart1:
      forRatingValuePart1?.nodes.length &&
      forRatingValuePart1.nodes.map((rating, i) => {
        return {
          index: i,
          rating: rating.rating,
        }
      }),
    reviewsForRatingValuePart2:
      forRatingValuePart2?.nodes.length &&
      forRatingValuePart2.nodes.map((rating, i) => {
        return {
          index: i,
          rating: rating.rating,
        }
      }),
    reviewsQuery:
      query1?.nodes.length &&
      query1.nodes.map((review, i) => {
        return {
          index: i,
          id: review.id,
          sourceType: review.sourceType,
          reviewDate: review.reviewDate,
          rating: review.rating,
          reviewerFirstName: review.reviewer.firstName,
          reviewerLastName: review.reviewer.lastName,
          comments: review.comments,
          businessName: review.businessName,
        }
      }),
    offers: data.offersSchema?.map(offer => {
      return {
        offer: offer.offerSchema,
        url: offer.urlSchema,
      }
    }),
    awards: data.awardsSchema?.map(award => {
      return {
        award: award.awardSchema,
      }
    }),
    brands: data.brandsSchema?.map(brand => {
      return {
        brand: brand.brandSchema,
      }
    }),
    certifications: data.certificationsSchema?.map(certification => {
      return {
        certification: certification.certificationSchema,
      }
    }),
    languages: data.languagesSchema?.map(language => {
      return {
        language: language.languageSchema,
      }
    }),
    organizations: data.organizationsSchema?.map(organization => {
      return {
        organization: organization.organizationSchema,
      }
    }),
    socialProfilesDirectoryListings:
      data.socialMediaProfilesAndDirectoryListingsSchema?.map(item => {
        return {
          socialProfileDirectory:
            item.socialMediaProfileOrDirectoryListingSchema,
        }
      }),
  }
}

export function useButtonTextData() {
  const buttonText = useButtonTextQuery()

  const finalButtonTextData = {
    buttonText: buttonText.commonItemsButtonText,
  }
  return finalButtonTextData
}

export function useHeaderData() {
  const header = useHeaderQuery()

  const finalHeaderData = {
    logo: header.commonItemsLogo.localFile.childImageSharp.gatsbyImageData,
    phoneNumber: header.commonItemsPhoneNumber,
    textColored: header.commonItemsHeaderTextColored,
    textWhite: header.commonItemsHeaderTextWhite,
  }
  return finalHeaderData
}

export function useHomeBlogsData() {
  const { homeBlogWpPage, homeBlogAllWpPost } = useHomeBlogsQuery()

  const finalHomeBlogsData = {
    heading: homeBlogWpPage.homeBlog.homeBlogHeading,
    subheading: homeBlogWpPage.homeBlog.homeBlogSubheading,
    blogPosts:
      homeBlogAllWpPost?.nodes.length &&
      homeBlogAllWpPost.nodes.map((blogPost, i) => {
        return {
          index: i,
          featuredImage:
            blogPost.featuredImage?.node.localFile.childImageSharp
              .gatsbyImageData,
          altText: blogPost.featuredImageAltText.featuredImageAltText,
          title: blogPost.title,
          excerpt: blogPost.excerpt,
          datePublished: blogPost.date,
          link: blogPost.link,
        }
      }),
  }
  return finalHomeBlogsData
}

export function useHomeCallout() {
  const homeCallout = useHomeCalloutQuery()

  const finalHomeCalloutData = {
    backgroundImageMobile:
      homeCallout.calloutBackgroundImageMobile?.localFile?.childImageSharp
        .gatsbyImageData,
    backgroundImageDesktop:
      homeCallout.calloutBackgroundImageDesktop?.localFile?.childImageSharp
        .gatsbyImageData,
    altText: homeCallout.calloutBackgroundImageAltText,
    calloutPercent: homeCallout.calloutPercent,
    calloutTitle: homeCallout.calloutTitle,
    calloutLinkTitle: homeCallout.calloutLink.title,
    calloutLink: homeCallout.calloutLink.url,
    calloutLinkIcon:
      homeCallout?.calloutActionIcon?.localFile?.childSvg?.content?.data,
  }

  return finalHomeCalloutData
}

export function useHomeCouponsData() {
  const homeCouponsData = useHomeCouponsQuery()

  const finalHomeCouponsData = {
    backgroundImage:
      homeCouponsData.couponsBackgroundImage.localFile.childImageSharp
        .gatsbyImageData,
    altText: homeCouponsData.couponsBackgroundImageAltText,
    heading: homeCouponsData.couponsHeading,
    coupons:
      homeCouponsData.couponsCoupons?.length &&
      homeCouponsData.couponsCoupons.map((coupon, i) => {
        return {
          index: i,
          content: coupon.couponsContent,
          discount: coupon.couponsAmountOff,
          percentageOrFixedAmount: coupon.couponsPercentageOrFixedAmount,
          couponCodeText: coupon.couponsCouponCodeText,
          couponCode: coupon.couponsCouponCode,
        }
      }),
  }
  return finalHomeCouponsData
}

export function useBlogOverviewBlogsData(data) {
  const finalData =
    data.allWpPost?.nodes.length &&
    data.allWpPost.nodes.map((blogPost, i) => {
      return {
        index: i,
        featuredImage:
          blogPost.featuredImage?.node.localFile.childImageSharp
            .gatsbyImageData,
        altText: blogPost.featuredImageAltText.featuredImageAltText,
        title: blogPost.title,
        excerpt: blogPost.excerpt,
        datePublished: blogPost.date,
        link: blogPost.link,
      }
    })

  return finalData
}

export function useBlogPostData(data) {
  const postData = data.wpPost
  const finalPostData = {
    featuredImage:
      postData.featuredImage?.node.localFile.childImageSharp.gatsbyImageData,
    altText: postData.featuredImageAltText.featuredImageAltText,
    title: postData.title,
    content: postData.content,
  }
  return finalPostData
}

export function useInteriorCalloutData() {
  const interiorCallout = useInteriorCalloutQuery()

  const finalInteriorCalloutData = {
    text: interiorCallout.commonItemsInteriorCalloutText,
    linkText: interiorCallout.commonItemsInteriorCalloutLink.title,
    link: interiorCallout.commonItemsInteriorCalloutLink.url,
    image:
      interiorCallout.commonItemsInteriorCalloutImage.localFile.childImageSharp
        .gatsbyImageData,
    altText: interiorCallout.commonItemsInteriorCalloutImageAltText,
  }
  return finalInteriorCalloutData
}

export function useMainMenuImages() {
  const { mainMenuDropdownImages } = useMainMenuQuery()
  const menuImages =
    mainMenuDropdownImages.mainMenuImages.mainMenuImageContainer?.length &&
    mainMenuDropdownImages.mainMenuImages.mainMenuImageContainer.map(
      (image, i) => {
        return {
          index: i,
          image: image.mainMenuImage.localFile.childImageSharp.gatsbyImageData,
          altText: image.mainMenuAltText,
        }
      }
    )

  return menuImages
}

export function useSidebarMenuTitle() {
  const { sidebarMenuTitle } = useMainMenuQuery()
  const sidebarMenuTitleData = {
    sidebarMenuTitle:
      sidebarMenuTitle.acfOptionsCommonItems.commonItems
        .commonItemsSidebarMenuTitle,
  }
  return sidebarMenuTitleData
}
export function useMainMenuItems() {
  const { mainMenuItems } = useMainMenuQuery()

  let topLevelData =
    mainMenuItems?.nodes.length &&
    mainMenuItems.nodes.map((topLevelItem, i) => {
      return {
        index: i,
        item: topLevelItem,
        id: topLevelItem.id,
        label: topLevelItem.label,
        path: topLevelItem.path,
        icon: topLevelItem.menuIcons.menuIconsIcon?.localFile.childSvg.content
          .data,
        className: topLevelItem.cssClasses,
        secondLevel:
          topLevelItem.childItems?.nodes.length &&
          topLevelItem.childItems.nodes.map((secondLevelItem, i) => {
            return {
              index: i,
              id: secondLevelItem.id,
              label: secondLevelItem.label,
              path: secondLevelItem.path,
              thirdLevel:
                secondLevelItem.childItems?.nodes.length &&
                secondLevelItem.childItems.nodes.map((thirdLevelItem, i) => {
                  return {
                    index: i,
                    label: thirdLevelItem.label,
                    path: thirdLevelItem.path,
                  }
                }),
            }
          }),
      }
    })

  return topLevelData
}

export function PageSpecificSchemaFunction(data) {
  const pageData = data.wpPage !== undefined ? data.wpPage : data.wpPost
  const pageSpecificSchemaData = usePageSpecificSchemaQuery().wp

  return {
    businessName:
      pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.businessName,
    businessType:
      pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.businessType,
    phoneNumber:
      pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.phoneNumber,
    streetAddress:
      pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.streetAddress,
    city: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.city,
    state: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.state,
    zipCode: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.zipCode,
    logo: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.logo.localFile
      .publicURL,
    blogPageUrl:
      pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.blogPageUrl,
    aboutPageUrl:
      pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.aboutPageUrl,
    contactPageUrl:
      pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.contactPageUrl,
    siteUrl: data.site.siteMetadata.siteUrl,
    uri: pageData !== undefined ? pageData.uri : "/blog/",
    title: pageData !== undefined ? pageData.title : "Blog",
    datePublished: pageData?.date,
    dateModified: pageData?.modified,
    image:
      pageData?.seo?.opengraphImage !== null
        ? // eslint-disable-next-line no-unsafe-optional-chaining
          data.site.siteMetadata.siteUrl +
          pageData?.seo?.opengraphImage.localFile.publicURL
        : "/blank.jpg",
    author: `${pageData?.author?.node.firstName} ${pageData?.author?.node.lastName}`,
    categories:
      pageData?.categories?.nodes.length &&
      pageData?.categories.nodes.map((category, i) => {
        return {
          index: i,
          name: category.name,
        }
      }),
    tags:
      pageData?.tags?.nodes.length &&
      pageData?.tags.nodes.map((tag, i) => {
        return {
          index: i,
          name: tag.name,
        }
      }),
    articleBody: pageData?.content,
    post: data.wpPost !== undefined,
    videos:
      pageData?.nexvelschemapagesposts?.videos?.length &&
      pageData?.nexvelschemapagesposts?.videos.map((video, i) => {
        return {
          index: i,
          title: video.title,
          url: video.url,
          description: video.description,
          thumbnailImage: video.thumbnailImage.localFile.publicURL,
          uploadDate: video.uploadDate,
        }
      }),
    questionsAndAnswers:
      pageData?.nexvelschemapagesposts?.questionsAndAnswers?.length &&
      pageData?.nexvelschemapagesposts?.questionsAndAnswers.map((qanda, i) => {
        return {
          index: i,
          question: qanda.question,
          answer: qanda.answer,
        }
      }),
    maps:
      pageData?.nexvelschemapagesposts?.maps?.length &&
      pageData?.nexvelschemapagesposts?.maps.map((map, i) => {
        return {
          index: i,
          url: map.mapUrl,
        }
      }),
    digitalDocuments:
      pageData?.nexvelschemapagesposts?.digitaldocuments?.length &&
      pageData?.nexvelschemapagesposts?.digitaldocuments.map((document, i) => {
        return {
          index: i,
          title: document.title,
        }
      }),
    images:
      pageData?.nexvelschemapagesposts?.images?.length &&
      pageData?.nexvelschemapagesposts?.images.map((item, i) => {
        return {
          index: i,
          image: item.image.localFile.publicURL,
          date: item.image.date,
          description: item.image.description,
          title: item.image.title,
        }
      }),
    areaServedGeneral: data.wp.nexvelSchemaMarkup?.nexvelschema.cities.map(
      city => {
        return {
          city: city.city,
        }
      }
    ),
    areaServedPageSpecific:
      pageData?.nexvelschemapagesposts?.areaServedLandingPageSchema,
    serviceGeneral: data.wp.nexvelSchemaMarkup?.nexvelschema.services.map(
      service => {
        return {
          serviceName: service.service,
          relevantPage: service.linkToRelevantPageOnYourWebsite,
        }
      }
    ),
    servicePageSpecific: {
      serviceName:
        pageData?.nexvelschemapagesposts?.serviceSchema.serviceNameSchema,
      relevantPage:
        pageData?.nexvelschemapagesposts?.serviceSchema
          .linkToRelevantServicePageSchema,
    },
    keywords: pageData?.nexvelschemapagesposts?.keywordsSchema?.map(item => {
      return {
        keyword: item.keywordSchema,
      }
    }),
    localAwards: pageData?.nexvelschemapagesposts?.localAwardsSchema?.map(
      item => {
        return {
          localAward: item.localAwardSchema,
        }
      }
    ),
    latitude: pageData?.nexvelschemapagesposts?.latitudeSchema,
    longitude: pageData?.nexvelschemapagesposts?.longitudeSchema,
    relevantExternalPages:
      pageData?.nexvelschemapagesposts?.relevantExternalPagesSchema?.map(
        item => {
          return {
            relevantExternalPage: item.relevantExternalPageSchema,
          }
        }
      ),
  }
}

export function useForReviewsWidget() {
  const { forRatingValuePart1, forRatingValuePart2 } = useGeneralSchemaQuery()
  const finalReviewsWidgetData = {
    reviewsForRatingValuePart1:
      forRatingValuePart1?.nodes.length &&
      forRatingValuePart1.nodes.map((rating, i) => {
        return {
          index: i,
          rating: rating.rating,
        }
      }),
    reviewsForRatingValuePart2:
      forRatingValuePart2?.nodes.length &&
      forRatingValuePart2.nodes.map((rating, i) => {
        return {
          index: i,
          rating: rating.rating,
        }
      }),
  }
  return finalReviewsWidgetData
}

export function useServiceAreasData() {
  const serviceAreasData = useServiceAreasQuery()

  const finalServiceAreasData = {
    mapUrlMobile:
      serviceAreasData.serviceAreasMapUrlMobile?.localFile?.childImageSharp
        ?.gatsbyImageData,
    mapUrlDesktop:
      serviceAreasData.serviceAreasMapUrlDesktop?.localFile?.childImageSharp
        ?.gatsbyImageData,
    heading: serviceAreasData.serviceAreasHeading,
    serviceAreas: (serviceAreasData.serviceAreasAreas || []).map(
      (serviceArea, i) => {
        return {
          index: i,
          label: serviceArea.serviceAreasLabel,
          locations: serviceArea.serviceAreasLocations,
        }
      }
    ),
    locationIcon:
      serviceAreasData.locationIcon?.localFile?.childSvg?.content?.data,
  }
  return finalServiceAreasData
}

export function useServicesData() {
  const servicesData = useServicesQuery()

  const finalServicesData = {
    backgroundImageDesktop:
      servicesData.servicesBackgroundImageDesktop?.localFile.childImageSharp
        .gatsbyImageData,
    backgroundImageMobile:
      servicesData.servicesBackgroundImageMobile?.localFile.childImageSharp
        .gatsbyImageData,
    altText: servicesData.servicesBackgroundImageAltText,
    heading: servicesData.servicesHeading,
    subheading: servicesData.servicesSubheading,
    serviceTabs:
      servicesData.servicesServiceTabs?.length &&
      servicesData.servicesServiceTabs.map((topLevelService, i) => {
        return {
          index: i,
          icon: topLevelService.servicesTabIcon.localFile.childSvg.content.data,
          label: topLevelService.servicesTabLabel,
          content: topLevelService.servicesTabContent,
          services:
            topLevelService.servicesTabServices?.length &&
            topLevelService.servicesTabServices.map((innerService, i) => {
              return {
                index: i,
                icon: innerService.servicesTabServicesIcon.localFile.childSvg
                  .content.data,
                label: innerService.servicesTabServicesLabel,
                image:
                  innerService.servicesTabServicesImage.localFile
                    .childImageSharp.gatsbyImageData,
                altText: innerService.servicesTabServicesImageAltText,
                content: innerService.servicesTabServicesContent,
                linkText: innerService.servicesTabServicesLink.title,
                link: innerService.servicesTabServicesLink.url,
              }
            }),
        }
      }),
  }
  return finalServicesData
}

export function useTestimonialsData() {
  const { testimonialsWpPage, testimonialsAllBirdeyedata } =
    useTestimonialsQuery()

  const finalTestimonialsData = {
    backgroundVideoUrl:
      testimonialsWpPage.testimonials.testimonialsBackgroundVideoUrl.url,
    playButton:
      testimonialsWpPage.testimonials.testimonialsPlayButton.localFile.childSvg
        .content.data,
    fullVideoUrl: testimonialsWpPage.testimonials.testimonialsFullVideoUrl.url,
    cardContent: testimonialsWpPage.testimonials.testimonialsVideoCardContent,
    heading: testimonialsWpPage.testimonials.testimonialsHeading,
    subheading: testimonialsWpPage.testimonials.testimonialsSubheading,
    h1: testimonialsWpPage.testimonials.testimonialsH1,
    content: testimonialsWpPage.testimonials.testimonialsContent,
    linkText: testimonialsWpPage.testimonials.testimonialsLink?.title,
    link: testimonialsWpPage.testimonials.testimonialsLink?.url,
    h1: testimonialsWpPage.testimonials.testimonialsH1,
    videoBackgroundImage:
      testimonialsWpPage.testimonials.videoBackgroundImage?.localFile
        ?.childImageSharp?.gatsbyImageData,
    testimonials:
      testimonialsAllBirdeyedata?.nodes.length &&
      testimonialsAllBirdeyedata.nodes.map((testimonial, i) => {
        return {
          index: i,
          source: testimonial.sourceType,
          review: testimonial.comments,
          reviewer:
            testimonial.reviewer.firstName +
            ` ` +
            testimonial.reviewer.lastName,
          rating: testimonial.rating,
        }
      }),
  }
  return finalTestimonialsData
}

export function useFinalTopBarMenuData() {
  const data = useTopBarMenuQuery()
  const finalData =
    data?.nodes.length &&
    data.nodes.map((menuItem, i) => {
      return {
        index: i,
        label: menuItem.label,
        path: menuItem.path,
      }
    })

  return finalData
}

export function useWelcomeData() {
  const welcomeData = useWelcomeQuery()

  const finalWelcomeData = {
    heading: welcomeData.welcomeHeading,
    subheading: welcomeData.welcomeSubheading,
    content: welcomeData.welcomeContent,
    linkText: welcomeData.welcomeLink?.title,
    link: welcomeData.welcomeLink?.url,
    backgroundImageMobile:
      welcomeData.welcomeBackgroundImageMobile.localFile.childImageSharp
        ?.gatsbyImageData,
    backgroundImageDesktop:
      welcomeData.welcomeBackgroundImageDesktop.localFile.childImageSharp
        ?.gatsbyImageData,
    altText: welcomeData.welcomeBackgroundImageAltText,
    images:
      welcomeData.welcomeImages?.length &&
      welcomeData.welcomeImages.map((image, i) => {
        return {
          index: i,
          image: image.welcomeImage.localFile.childImageSharp.gatsbyImageData,
          altText: image.welcomeImageAltText,
        }
      }),
  }
  return finalWelcomeData
}

export function useWhyChooseData() {
  const whyChooseData = useWhyChooseQuery()

  const finalWhyChooseData = {
    backgroundImageMobile:
      whyChooseData.whyChooseBackgroundImageMobile.localFile.childImageSharp
        .gatsbyImageData,
    backgroundImageDesktop:
      whyChooseData.whyChooseBackgroundImageDesktop.localFile.childImageSharp
        .gatsbyImageData,
    backgroundImageAltText: whyChooseData.whyChooseBackgroundImageAltText,
    heading: whyChooseData.whyChooseHeading,
    subheading: whyChooseData.whyChooseSubheading,
    smallText: whyChooseData.whyChooseSmallText,
    imageMobile:
      whyChooseData.whyChooseImageMobile.localFile.childImageSharp
        .gatsbyImageData,
    imageDesktop:
      whyChooseData.whyChooseImageDesktop.localFile.childImageSharp
        .gatsbyImageData,
    altText: whyChooseData.whyChooseImageAltText,
    reasons:
      whyChooseData.whyChooseReasons?.length &&
      whyChooseData.whyChooseReasons.map((reason, i) => {
        return {
          index: i,
          icon: reason.whyChooseIcon.localFile.childSvg.content.data,
          reason: reason.whyChooseText,
        }
      }),
  }
  return finalWhyChooseData
}

export function useHomeFinancingData() {
  const homeFinancingData = useHomeFinancingQuery()

  return {
    backgroundImageMobile:
      homeFinancingData.financingBackgroundImageMobile?.localFile
        ?.childImageSharp?.gatsbyImageData,
    backgroundImageDesktop:
      homeFinancingData.financingBackgroundImageDesktop?.localFile
        ?.childImageSharp?.gatsbyImageData,
    foregroundImage:
      homeFinancingData.financingForegroundImage?.localFile?.childImageSharp
        ?.gatsbyImageData,
    altText: homeFinancingData.financingForegroundImageAltText,
    content: homeFinancingData.financingContent,
    phoneNumber: homeFinancingData.phoneNumber,
    phoneIcon:
      homeFinancingData.phoneIcon?.localFile?.childImageSharp?.gatsbyImageData,
  }
}
